// Add / Edit CarrierContact modal — FE-SCRM-030 Steps 8 + 9.
//
// One component, two modes. Required: first_name, last_name,
// role_type. Status transitions ``active → left_carrier`` reveal the
// ``last_known_destination`` field (and the reverse transition clears
// it). Submitting calls the caller-provided handler so the modal
// stays presentation-only.

import { useEffect, useMemo, useState } from "react";

import { Modal } from "@/design-system";

import { CARRIER_CONTACT_ROLE_LABEL, formatUsPhone } from "./labels";
import {
  CARRIER_CONTACT_ROLE_TYPES,
  CARRIER_CONTACT_STATUSES,
  type CarrierContact,
  type CarrierContactRoleType,
  type CarrierContactStatus,
  type CarrierContactWriteInput,
} from "./types";


export type ContactFormMode = "create" | "edit";


interface Props {
  open: boolean;
  mode: ContactFormMode;
  initial?: CarrierContact | null;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (values: CarrierContactWriteInput) => void;
}


export function CarrierContactFormModal({
  open, mode, initial, isSubmitting, errorMessage, onClose, onSubmit,
}: Props) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [title, setTitle] = useState("");
  const [role, setRole] = useState<CarrierContactRoleType>("other");
  const [territory, setTerritory] = useState("");
  const [carrierOffice, setCarrierOffice] = useState("");
  const [segmentFocus, setSegmentFocus] = useState("");
  const [status, setStatus] = useState<CarrierContactStatus>("active");
  const [lastKnownDestination, setLastKnownDestination] = useState("");
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFirstName(initial?.first_name ?? "");
    setLastName(initial?.last_name ?? "");
    setEmail(initial?.email ?? "");
    setPhone(formatUsPhone(initial?.phone ?? ""));
    setMobile(formatUsPhone(initial?.mobile ?? ""));
    setTitle(initial?.title ?? "");
    setRole(initial?.role_type ?? "other");
    setTerritory(initial?.territory ?? "");
    setCarrierOffice(initial?.carrier_office ?? "");
    setSegmentFocus(initial?.segment_focus ?? "");
    setStatus(initial?.status ?? "active");
    setLastKnownDestination(initial?.last_known_destination ?? "");
    setNotes(initial?.notes ?? "");
    setTouched(false);
  }, [open, initial]);

  // When the user toggles status away from ``left_carrier`` we wipe
  // the ``last_known_destination`` so the backend stores the cleaned
  // state. The spec calls this out explicitly so the UI doesn't ship
  // a stale destination on a contact that came back.
  useEffect(() => {
    if (status !== "left_carrier" && lastKnownDestination) {
      setLastKnownDestination("");
    }
    // ``lastKnownDestination`` intentionally absent from the deps so
    // a user typing into the field doesn't trigger a no-op re-clear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const firstNameError =
    touched && !firstName.trim() ? "First name is required" : null;
  const lastNameError =
    touched && !lastName.trim() ? "Last name is required" : null;
  const emailError = useMemo(() => {
    if (!touched || !email.trim()) return null;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      return "Enter a valid email address";
    }
    return null;
  }, [email, touched]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (
      !firstName.trim() || !lastName.trim() || emailError
    ) return;
    onSubmit({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      role_type: role,
      email: email.trim() || null,
      phone: phone.trim() || null,
      mobile: mobile.trim() || null,
      title: title.trim() || null,
      territory: territory.trim() || null,
      carrier_office: carrierOffice.trim() || null,
      segment_focus: segmentFocus.trim() || null,
      status,
      last_known_destination:
        status === "left_carrier"
          ? lastKnownDestination.trim() || null
          : null,
      notes: notes.trim() || null,
    });
  }

  const title_ = mode === "create" ? "Add contact" : "Edit contact";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title_}
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="contact-form-cancel"
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="contact-form"
            disabled={isSubmitting}
            data-testid="contact-form-submit"
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90 disabled:opacity-60"
          >
            {isSubmitting
              ? (mode === "create" ? "Adding…" : "Saving…")
              : (mode === "create" ? "Add contact" : "Save changes")}
          </button>
        </>
      }
    >
      <form
        id="contact-form"
        data-testid="contact-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
      >
        {errorMessage && (
          <div
            data-testid="contact-form-error"
            role="alert"
            className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember"
          >
            {errorMessage}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" required>
            <input
              type="text"
              data-testid="contact-form-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={() => setTouched(true)}
              className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
            />
            {firstNameError && (
              <p className="mt-1 text-xs text-ember" role="alert">
                {firstNameError}
              </p>
            )}
          </Field>
          <Field label="Last name" required>
            <input
              type="text"
              data-testid="contact-form-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={() => setTouched(true)}
              className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
            />
            {lastNameError && (
              <p className="mt-1 text-xs text-ember" role="alert">
                {lastNameError}
              </p>
            )}
          </Field>
        </div>

        <Field label="Title">
          <input
            type="text"
            data-testid="contact-form-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
          />
        </Field>

        <Field label="Role" required>
          <select
            data-testid="contact-form-role"
            value={role}
            onChange={(e) => setRole(e.target.value as CarrierContactRoleType)}
            className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
          >
            {CARRIER_CONTACT_ROLE_TYPES.map((r) => (
              <option key={r} value={r}>
                {CARRIER_CONTACT_ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Email">
            <input
              type="email"
              data-testid="contact-form-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
            />
            {emailError && (
              <p className="mt-1 text-xs text-ember" role="alert">
                {emailError}
              </p>
            )}
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              data-testid="contact-form-phone"
              value={phone}
              onChange={(e) => setPhone(formatUsPhone(e.target.value))}
              placeholder="(555) 555-1234"
              className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Mobile">
            <input
              type="tel"
              data-testid="contact-form-mobile"
              value={mobile}
              onChange={(e) => setMobile(formatUsPhone(e.target.value))}
              placeholder="(555) 555-1234"
              className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
            />
          </Field>
          <Field label="Territory">
            <input
              type="text"
              data-testid="contact-form-territory"
              value={territory}
              onChange={(e) => setTerritory(e.target.value)}
              className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Carrier office">
            <input
              type="text"
              data-testid="contact-form-carrier-office"
              value={carrierOffice}
              onChange={(e) => setCarrierOffice(e.target.value)}
              className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
            />
          </Field>
          <Field label="Segment focus">
            <input
              type="text"
              data-testid="contact-form-segment-focus"
              value={segmentFocus}
              onChange={(e) => setSegmentFocus(e.target.value)}
              className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
            />
          </Field>
        </div>

        <Field label="Status">
          <select
            data-testid="contact-form-status"
            value={status}
            onChange={(e) =>
              setStatus(e.target.value as CarrierContactStatus)
            }
            className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
          >
            {CARRIER_CONTACT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </option>
            ))}
          </select>
        </Field>

        {status === "left_carrier" && (
          <Field label="Last known destination">
            <input
              type="text"
              data-testid="contact-form-last-known-destination"
              value={lastKnownDestination}
              onChange={(e) => setLastKnownDestination(e.target.value)}
              placeholder="Where did they go? (optional)"
              className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
            />
          </Field>
        )}

        <Field label="Notes">
          <textarea
            data-testid="contact-form-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
          />
        </Field>
      </form>
    </Modal>
  );
}


function Field({
  label, required, children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs font-medium text-text-secondary">
        {label}
        {required && <span className="text-ember"> *</span>}
      </span>
      {children}
    </label>
  );
}
